import { supabase } from '../../lib/supabase'
import type { LeaveType, LeaveRequestState } from '../agent/leaveRequestStore'

// Every leave request a signed-in supervisor (or administrator) can see,
// replacing leaveApprovalQueue.ts's own hardcoded LEAVE_QUEUE and the
// read half of leaveDecisionStore.ts: those were two separate stores
// joined by requestId because nothing behind them was real yet. The real
// leave_requests and decisions tables are already joined, so one list
// with its own state and (once decided) decision fields replaces both;
// every consumer filters this locally the same way it already filtered
// two lists into one.
export type TeamLeaveRequest = {
  id: string
  employeeId: string
  employeeName: string
  teamId: string
  teamName: string
  startDate: string
  endDate: string
  // Every date this request covers, ISO, narrowed to Approve with
  // modification's own subset once decided: the planning board reads
  // this to know exactly which of the employee's cells to show as leave.
  dates: string[]
  // The request's own full range, never narrowed: what the approval
  // inbox's own "Approved X of Y requested days" compares dates against,
  // and what its own modification panel offers a checkbox for each of,
  // since dates itself already equals this before any decision exists
  // but stops matching once one does.
  requestedDates: string[]
  fullDay: boolean
  startTime?: string
  endTime?: string
  leaveType: LeaveType
  comment?: string
  state: LeaveRequestState
  dateRange: string
  sortDate: string
  submittedAt: string
  decidedAt?: string
  decisionComment?: string
  decidedByName?: string
}

function parseISO(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function formatDateRange(startDate: string, endDate: string): string {
  const start = parseISO(startDate)
  const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (startDate === endDate) return startLabel
  const end = parseISO(endDate)
  const endLabel =
    start.getMonth() === end.getMonth()
      ? `${end.getDate()}`
      : end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${startLabel}–${endLabel}`
}

// Inclusive of both ends, in the employee's own local calendar: a
// multi-day full-day request covers every date in between, not only the
// two it was submitted with.
export function expandDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = []
  let cursor = parseISO(startDate)
  const end = parseISO(endDate)
  while (cursor <= end) {
    dates.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
    )
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
  }
  return dates
}

export type EmployeeTeamInfo = { employeeName: string; teamId: string; teamName: string }

// employee_assignments_read_team (leave_requests.sql) already scopes this
// to the signed-in supervisor's own teams, and to an administrator, every
// team; no explicit team filter is needed here for the same reason
// leave_requests_read_team needs none on the query below. Exported for
// scheduleSubmissionQueue.ts's own getTeamScheduleSubmissions, which needs
// the same resolution and would otherwise carry a second copy of it.
export async function getEmployeeTeamMap(): Promise<Map<string, EmployeeTeamInfo>> {
  const today = new Date().toISOString().slice(0, 10)
  const { data, error } = await supabase
    .from('employee_assignments')
    .select('employee_id, team_id, teams(name), employees(display_name)')
    .eq('active', true)
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
  if (error || !data) return new Map()

  const map = new Map<string, EmployeeTeamInfo>()
  for (const row of data as unknown as {
    employee_id: string
    team_id: string
    teams: { name: string } | null
    employees: { display_name: string } | null
  }[]) {
    map.set(row.employee_id, {
      employeeName: row.employees?.display_name ?? '',
      teamId: row.team_id,
      teamName: row.teams?.name ?? '',
    })
  }
  return map
}

type LeaveRequestDecisionRow = {
  decision: string
  comment: string | null
  decided_at: string
  decided_by_name: string
  modified_dates: string[] | null
}

type LeaveRequestRow = {
  id: string
  employee_id: string
  start_date: string
  end_date: string
  full_day: boolean
  start_time: string | null
  end_time: string | null
  comment: string | null
  state: LeaveRequestState
  created_at: string
  leave_types: { name: string } | null
  decisions: LeaveRequestDecisionRow[] | null
}

const LEAVE_REQUEST_COLUMNS =
  'id, employee_id, start_date, end_date, full_day, start_time, end_time, comment, state, created_at, leave_types(name), decisions(decision, comment, decided_at, decided_by_name, modified_dates)'

export async function getTeamLeaveRequests(): Promise<TeamLeaveRequest[]> {
  const employeeTeams = await getEmployeeTeamMap()

  const { data, error } = await supabase
    .from('leave_requests')
    .select(LEAVE_REQUEST_COLUMNS)
    .order('start_date', { ascending: true })
  if (error || !data) return []

  return (data as unknown as LeaveRequestRow[]).flatMap((row) => {
    // Not on a team the signed-in supervisor has access to: RLS itself
    // already keeps such a row out of `data`, so this only ever guards a
    // race between the two queries above, never a real gap.
    const teamInfo = employeeTeams.get(row.employee_id)
    if (!teamInfo) return []

    // A reopened, resubmitted request can gather a second decision, the
    // same reason leaveRequestStore.ts's own toLeaveRequest reads the
    // latest by decided_at rather than assuming PostgREST's own order:
    // row.decisions?.[0] would otherwise still read an earlier Return
    // once a supervisor has since decided the resubmission.
    const latest = (row.decisions ?? []).reduce<LeaveRequestDecisionRow | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    // A decision a supervisor has since reversed no longer stands: the request is
    // pending again, with no decision to show.
    const decision = latest?.decision === 'reverse' ? undefined : latest
    const requestedDates = expandDateRange(row.start_date, row.end_date)
    const dates = decision?.modified_dates ?? requestedDates

    return [
      {
        id: row.id,
        employeeId: row.employee_id,
        employeeName: teamInfo.employeeName,
        teamId: teamInfo.teamId,
        teamName: teamInfo.teamName,
        startDate: row.start_date,
        endDate: row.end_date,
        dates,
        requestedDates,
        fullDay: row.full_day,
        startTime: row.start_time ?? undefined,
        endTime: row.end_time ?? undefined,
        leaveType: (row.leave_types?.name as LeaveType | undefined) ?? 'Other approved absence',
        comment: row.comment ?? undefined,
        state: row.state,
        dateRange: formatDateRange(row.start_date, row.end_date),
        sortDate: row.start_date,
        submittedAt: row.created_at,
        decidedAt: decision?.decided_at,
        decisionComment: decision?.comment ?? undefined,
        decidedByName: decision?.decided_by_name,
      },
    ]
  })
}
